import { createFileRoute } from "@tanstack/react-router";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { useMemo, useRef, useCallback, useState } from "react";
import DataTable from "@/components/data-table";
import { useSeedCounts } from "@/hooks/use-mailbox";
import { dateFormatter } from "@/lib/utils";
import { SearchInput } from "@/components/ui/search-input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Mail, Download } from "lucide-react";
import type { SeedCountData } from "@/services/mailbox.service";
import { useServices } from "@/services/hooks/useServices";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AuthProvider } from "@/types/mailbox.types";
import { authProviderIcons } from "@/pages/mailbox/esp-providers";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export const Route = createFileRoute("/_private/seed-list/seed-counts")({
  component: SeedCountsPage,
});

function SeedCountsPage() {
  const gridRef = useRef<AgGridReact>(null);
  const { data: seedCounts, isLoading } = useSeedCounts();
  const { mailboxService } = useServices();
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());
  const [downloadingAll, setDownloadingAll] = useState<Set<string>>(new Set());

  // Modal state for per-row downloads
  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
  const [activeRow, setActiveRow] = useState<SeedCountData | null>(null);
  const [isReplacementDialogOpen, setIsReplacementDialogOpen] = useState(false);

  // Download all replacements function
  const handleDownloadAllReplacements = useCallback(
    async (id: string, email: string) => {
      const downloadKey = `${id}-all-replacements`;
      try {
        setDownloadingAll((prev) => new Set(prev).add(downloadKey));
        
        const response = await mailboxService.downloadAllReplacements(id);
        
        const blob = new Blob([response as unknown as BlobPart], {
          type: "text/csv; charset=utf-8",
        });

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        const date = new Date().toISOString().split("T")[0];
        const sanitizedEmail = email.replace(/[^a-zA-Z0-9.-]/g, "_");
        a.href = url;
        a.download = `${sanitizedEmail}_all_replacements_${date}.csv`;

        // Trigger download
        document.body.appendChild(a);
        a.click();

        // Cleanup
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        toast.success("All replacements downloaded successfully");
      } catch (error) {
        toast.error("Failed to download all replacements");
        console.error("Download error:", error);
      } finally {
        setDownloadingAll((prev) => {
          const newSet = new Set(prev);
          newSet.delete(downloadKey);
          return newSet;
        });
      }
    },
    [mailboxService]
  );

  // Download all seeds function
  const handleDownloadAllSeeds = useCallback(
    async (id: string, email: string) => {
      const downloadKey = `${id}-all-seeds`;
      try {
        setDownloadingAll((prev) => new Set(prev).add(downloadKey));
        
        const response = await mailboxService.downloadAllSeeds(id);
        
        const blob = new Blob([response as unknown as BlobPart], {
          type: "text/csv; charset=utf-8",
        });

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        const date = new Date().toISOString().split("T")[0];
        const sanitizedEmail = email.replace(/[^a-zA-Z0-9.-]/g, "_");
        a.href = url;
        a.download = `${sanitizedEmail}_all_seeds_${date}.csv`;

        // Trigger download
        document.body.appendChild(a);
        a.click();

        // Cleanup
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        toast.success("All seeds downloaded successfully");
      } catch (error) {
        toast.error("Failed to download all seeds");
        console.error("Download error:", error);
      } finally {
        setDownloadingAll((prev) => {
          const newSet = new Set(prev);
          newSet.delete(downloadKey);
          return newSet;
        });
      }
    },
    [mailboxService]
  );

  // Download function
  const handleDownload = useCallback(
    async (id: string, email: string, espType: string, replacementAvailable: boolean, isReplacement: boolean) => {
      const downloadKey = `${id}-${espType}-${isReplacement ? 'replacement' : 'seeds'}`;
      try {
        setDownloadingIds((prev) => new Set(prev).add(downloadKey));
        
        // Use replacement API if available, otherwise use regular seeds API
        const isUsingReplacement = isReplacement && replacementAvailable;
        const response = isUsingReplacement 
          ? await mailboxService.downloadReplacements(id, espType)
          : await mailboxService.downloadSeeds(id, espType);
        
        const blob = new Blob([response as unknown as BlobPart], {
          type: "text/csv; charset=utf-8",
        });

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        const date = new Date().toISOString().split("T")[0];
        // Sanitize email for filename (replace special characters)
        const sanitizedEmail = email.replace(/[^a-zA-Z0-9.-]/g, "_");
        a.href = url;
        const fileType = isUsingReplacement ? 'replacements' : 'seeds';
        a.download = `${sanitizedEmail}_${espType.toLowerCase()}_${fileType}_${date}.csv`;

        // Trigger download
        document.body.appendChild(a);
        a.click();

        // Cleanup
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        const fileLabel = isUsingReplacement ? 'replacements' : 'seeds';
        toast.success(`${espType} ${fileLabel} downloaded successfully`);
      } catch (error) {
        const fileLabel = isReplacement ? 'replacements' : 'seeds';
        toast.error(`Failed to download ${espType} ${fileLabel}`);
        console.error("Download error:", error);
      } finally {
        setDownloadingIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(downloadKey);
          return newSet;
        });
      }
    },
    [mailboxService]
  );

  const providerOptions: {
    label: string;
    value: string;
    icon: React.ReactNode;
  }[] = [
    // {
    //   label: "Google Workspace",
    //   value: "google",
    //   icon: <img src="/googleIcon.png" alt="google icon" className="!size-5" />,
    // },
    {
      label: "Google Workspace",
      value: "google_bulk",
      icon: <img src="/googleIcon.png" alt="google icon" className="!size-5" />,
    },
    {
      label: "Microsoft Outlook",
      value: "microsoft",
      icon: (
        <img src="/microsoftIcon.png" alt="microsoft icon" className="!size-5" />
      ),
    },
    {
      label: "AWS SES SMTP",
      value: "aws_smtp",
      icon: <img src="/amazonIcon.png" alt="aws icon" className="!size-5" />,
    },
    {
      label: "AWS SES API",
      value: "aws_api",
      icon: <img src="/amazonIcon.png" alt="aws icon" className="!size-5" />,
    },
    {
      label: "SendGrid SMTP",
      value: "sendgrid_smtp",
      icon: (
        <img src="/sendGridIcon.png" alt="sendgrid icon" className="!size-5" />
      ),
    },
    {
      label: "SendGrid API",
      value: "sendgrid_api",
      icon: (
        <img src="/sendGridIcon.png" alt="sendgrid icon" className="!size-5" />
      ),
    },
    {
      label: "Private SMTP",
      value: "imap",
      icon: <Mail className="!size-5" />,
    },
  ];

  // Define column definitions
  const columnDefs: ColDef<SeedCountData>[] = useMemo(
    () => [
      {
        field: "email",
        headerName: "Seed Account",
        minWidth: 250,
      },
      {
        field: "senderEspType",
        headerName: "Sender ESP Type",
        minWidth: 150,
        cellRenderer: (params: { value: string }) => {
            return (
              <div className="flex justify-center items-center h-full">
                <Tooltip>
                  <TooltipTrigger asChild>
                    {authProviderIcons[params.value as keyof typeof authProviderIcons]?.icon}
                  </TooltipTrigger>
                  <TooltipContent>
                    {authProviderIcons[params.value as keyof typeof authProviderIcons]?.tooltip}
                  </TooltipContent>
                </Tooltip>
              </div>
            );
          },
      },
      {
        field: "seedCounts.gmail",
        headerName: "Gmail Seeds",
        minWidth: 120,
        cellRenderer: (params: { data: SeedCountData }) => (
          <span>{params.data.seedCounts?.gmail || 0}</span>
        ),
      },
      {
        field: "seedCounts.outlook",
        headerName: "Outlook Seeds",
        minWidth: 140,
        cellRenderer: (params: { data: SeedCountData }) => (
          <span>{params.data.seedCounts?.outlook || 0}</span>
        ),
      },
      {
        field: "seedCounts.yahoo",
        headerName: "Yahoo Seeds",
        minWidth: 120,
        cellRenderer: (params: { data: SeedCountData }) => (
          <span>{params.data.seedCounts?.yahoo || 0}</span>
        ),
      },
      {
        field: "seedCounts.Google-Workspace",
        headerName: "Google Workspace Seeds",
        minWidth: 140,
        cellRenderer: (params: { data: SeedCountData }) => (
          <span>{params.data.seedCounts?.["Google-Workspace"] || 0}</span>
        ),
      },
      {
        field: "seedCounts.Microsoft365",
        headerName: "Microsoft365 Seeds",
        minWidth: 120,
        cellRenderer: (params: { data: SeedCountData }) => (
          <span>{params.data.seedCounts?.Microsoft365 || 0}</span>
        ),
      },
      {
        field: "seedEspTypes",
        headerName: "Seed Assigned",
        minWidth: 150,
        cellRenderer: (params: { data: any }) => {
          return <span>{params.data.seedEspTypes.join(", ") || "-"}</span>;
        },
      },
      {
        field: "lastSeedUpdate",
        headerName: "Last Seed Update",
        minWidth: 200,
        cellRenderer: (params: { data: SeedCountData }) => (
          <span>{dateFormatter(params.data.lastSeedUpdate ?? Date.now())}</span>
        ),
      },
      {
        colId: "actions-replacements",
        headerName: "Replacements",
        sortable: false,
        filter: false,
        minWidth: 220,
        cellRenderer: (params: { data: SeedCountData }) => {
          const data = params.data;
          const hasReplacements = !!(
            data.replacementAvailability?.gmail ||
            data.replacementAvailability?.outlook ||
            data.replacementAvailability?.yahoo ||
            data.replacementAvailability?.["Google-Workspace"] ||
            data.replacementAvailability?.Microsoft365
          );
          return (
            <div className="flex items-center h-full">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                disabled={!hasReplacements}
                onClick={() => {
                  setActiveRow(data);
                  setIsReplacementDialogOpen(true);
                }}
              >
                <Download className="h-3 w-3 mr-1" />
                Download Replacements
              </Button>
            </div>
          );
        },
      },
      {
        colId: "actions",
        headerName: "Live Seeds",
        sortable: false,
        filter: false,
        minWidth: 160,
        cellRenderer: (params: { data: SeedCountData }) => {
          const data = params.data;
          return (
            <div className="flex items-center h-full">
              <Button
                size="sm"
                className="h-8 text-xs"
                onClick={() => {
                  setActiveRow(data);
                  setIsDownloadDialogOpen(true);
                }}
              >
                <Download className="h-3 w-3 mr-1" />
                Download
              </Button>
            </div>
          );
        },
      },
     
    ],
    [handleDownload, downloadingIds, handleDownloadAllReplacements, handleDownloadAllSeeds, downloadingAll]
  );

  // Handle search
  const onFilterTextBoxChanged = useCallback((searchTerm: string) => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption("quickFilterText", searchTerm);
    }
  }, []);

  // Calculate stats
  const stats = useMemo(() => {
    if (!seedCounts || !Array.isArray(seedCounts)) {
      return {
        totalSenders: 0,
        totalGmailSeeds: 0,
        totalOutlookSeeds: 0,
        totalYahooSeeds: 0,
        totalGoogleworkspaceSeeds: 0,
        totalMicrosoft365Seeds: 0,
        totalSeeds: 0,
      };
    }

    return seedCounts.reduce(
      (acc, item) => {
        const gmail = item.seedCounts?.gmail || 0;
        const outlook = item.seedCounts?.outlook || 0;
        const yahoo = item.seedCounts?.yahoo || 0;
        const googleWorkspace = item.seedCounts?.["Google-Workspace"] || 0;
        const microsoft365 = item.seedCounts?.Microsoft365 || 0;

        const perSenderTotal = gmail + outlook + yahoo + googleWorkspace + microsoft365;

        return {
          totalSenders: acc.totalSenders + 1,
          totalGmailSeeds: Math.max(acc.totalGmailSeeds, gmail),
          totalOutlookSeeds: Math.max(acc.totalOutlookSeeds, outlook),
          totalYahooSeeds: Math.max(acc.totalYahooSeeds, yahoo),
          totalGoogleworkspaceSeeds: Math.max(acc.totalGoogleworkspaceSeeds, googleWorkspace),
          totalMicrosoft365Seeds: Math.max(acc.totalMicrosoft365Seeds, microsoft365),
          totalSeeds: Math.max(acc.totalSeeds, perSenderTotal),
        };
      },
      {
        totalSenders: 0,
        totalGmailSeeds: 0,
        totalOutlookSeeds: 0,
        totalYahooSeeds: 0,
        totalGoogleworkspaceSeeds: 0,
        totalMicrosoft365Seeds: 0,
        totalSeeds: 0,
      }
    );
  }, [seedCounts]);

  return (
    <div className="space-y-4 w-full">
      <h1 className="page-heading">Seed Counts</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-4">
        {isLoading || !seedCounts
          ? Array.from({ length: 5 }).map((quarter, i) => (
              <Card key={i} className="w-full">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Skeleton className="size-8 shrink-0" />
                    <Skeleton className="h-5 w-40 sm:w-full" />
                  </div>
                  <Skeleton className="h-8 max-w-40 sm:w-full" />
                </CardContent>
              </Card>
            ))
          : [
              {
                id: "total-senders",
                title: "Total Senders",
                value: stats.totalSenders,
                icon: Mail,
              },
              {
                id: "total-gmail",
                title: "Gmail Seeds",
                value: stats.totalGmailSeeds,
                icon: Mail,
              },
              {
                id: "total-outlook",
                title: "Outlook Seeds",
                value: stats.totalOutlookSeeds,
                icon: Mail,
              },
              {
                id: "total-yahoo",
                title: "Yahoo Seeds",
                value: stats.totalYahooSeeds,
                icon: Mail,
              },
              {
                id: "total-google",
                title: "Google Workspace Seeds",
                value: stats.totalGoogleworkspaceSeeds,
                icon: Mail,
              },
              {
                id: "total-microsoft",
                title: "Microsoft 365 Seeds",
                value: stats.totalMicrosoft365Seeds,
                icon: Mail,
              },
              {
                id: "total-seeds",
                title: "Total Seeds",
                value: stats.totalSeeds,
                icon: Mail,
              },
            ].map((card) => (
              <Card key={card.id} className="p-4 space-y-1 sm:space-y-2">
                <CardHeader className="p-0">
                  <CardTitle className="text-sm font-medium">
                    <div className="flex items-center gap-1 sm:gap-2">
                      <div className="border rounded-lg bg-secondary size-8 flex items-center justify-center shrink-0">
                        <card.icon className="size-4" />
                      </div>
                      <span className="text-sm truncate">{card.title}</span>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="text-lg sm:text-xl md:text-2xl font-bold">
                    {card.value}
                  </div>
                </CardContent>
              </Card>
            ))}
      </div>

      {/* Search and Grid */}
      <div className="flex gap-2 items-center justify-between flex-wrap">
        <SearchInput
          onSearch={(text) => onFilterTextBoxChanged(text)}
          placeholder="Search by email..."
          className="max-w-none sm:w-full md:max-w-[300px]"
        />
      </div>

      <DataTable
        ref={gridRef}
        rowData={seedCounts ?? []}
        columnDefs={columnDefs}
        height={550}
        showPagination={true}
        defaultColDef={{
          editable: false,
          resizable: true,
          sortable: true,
          filter: true,
        }}
        paginationProps={{
          pageSize: 100,
          totalPages: Math.ceil((seedCounts?.length || 0) / 100),
          currentPage: 1,
          onPageChange: () => {},
          totalEntries: seedCounts?.length || 0,
          rowsPerPage: 100,
          onRowsPerPageChange: () => {},
        }}
        loading={isLoading}
        gridOptions={{
          animateRows: false,
          suppressColumnVirtualisation: true,
          suppressRowVirtualisation: true,
          rowBuffer: 1000,
          getRowId: (params) => params.data?.id,
          suppressPropertyNamesCheck: true,
          suppressScrollOnNewData: false,
          enableCellTextSelection: true,
          alwaysShowVerticalScroll: true,
        }}
      />

      {/* Download dialog (Seeds) */}
      <Dialog open={isDownloadDialogOpen} onOpenChange={setIsDownloadDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Download Seeds</DialogTitle>
          </DialogHeader>

          

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              variant="outline"
              className="h-10 justify-start"
              disabled={
                !activeRow?.seedCounts?.gmail ||
                !!(activeRow && downloadingIds.has(`${activeRow.id}-Gmail-seeds`))
              }
              onClick={() => {
                if (!activeRow) return;
                handleDownload(
                  activeRow.id,
                  activeRow.email,
                  "Gmail",
                  activeRow.replacementAvailability?.gmail || false,
                  false
                );
              }}
            >
              {activeRow && downloadingIds.has(`${activeRow.id}-Gmail-seeds`) ? (
                <span className="h-4 w-4 mr-2 inline-block animate-spin rounded-full border-2 border-current border-r-transparent" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {activeRow && downloadingIds.has(`${activeRow.id}-Gmail-seeds`) ? "Downloading..." : "Gmail Seeds"}
            </Button>
            <Button
              variant="outline"
              className="h-10 justify-start"
              disabled={
                !activeRow?.seedCounts?.outlook ||
                !!(activeRow && downloadingIds.has(`${activeRow.id}-Outlook-seeds`))
              }
              onClick={() => {
                if (!activeRow) return;
                handleDownload(
                  activeRow.id,
                  activeRow.email,
                  "Outlook",
                  activeRow.replacementAvailability?.outlook || false,
                  false
                );
              }}
            >
              {activeRow && downloadingIds.has(`${activeRow.id}-Outlook-seeds`) ? (
                <span className="h-4 w-4 mr-2 inline-block animate-spin rounded-full border-2 border-current border-r-transparent" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {activeRow && downloadingIds.has(`${activeRow.id}-Outlook-seeds`) ? "Downloading..." : "Outlook Seeds"}
            </Button>
            <Button
              variant="outline"
              className="h-10 justify-start"
              disabled={
                !activeRow?.seedCounts?.yahoo ||
                !!(activeRow && downloadingIds.has(`${activeRow.id}-Yahoo-seeds`))
              }
              onClick={() => {
                if (!activeRow) return;
                handleDownload(
                  activeRow.id,
                  activeRow.email,
                  "Yahoo",
                  activeRow.replacementAvailability?.yahoo || false,
                  false
                );
              }}
            >
              {activeRow && downloadingIds.has(`${activeRow.id}-Yahoo-seeds`) ? (
                <span className="h-4 w-4 mr-2 inline-block animate-spin rounded-full border-2 border-current border-r-transparent" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {activeRow && downloadingIds.has(`${activeRow.id}-Yahoo-seeds`) ? "Downloading..." : "Yahoo Seeds"}
            </Button>
            <Button
              variant="outline"
              className="h-10 justify-start"
              disabled={
                !activeRow?.seedCounts?.["Google-Workspace"] ||
                !!(activeRow && downloadingIds.has(`${activeRow.id}-Google-Workspace-seeds`))
              }
              onClick={() => {
                if (!activeRow) return;
                handleDownload(
                  activeRow.id,
                  activeRow.email,
                  "Google-Workspace",
                  activeRow.replacementAvailability?.["Google-Workspace"] || false,
                  false
                );
              }}
            >
              {activeRow && downloadingIds.has(`${activeRow.id}-Google-Workspace-seeds`) ? (
                <span className="h-4 w-4 mr-2 inline-block animate-spin rounded-full border-2 border-current border-r-transparent" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {activeRow && downloadingIds.has(`${activeRow.id}-Google-Workspace-seeds`) ? "Downloading..." : "Google Workspace Seeds"}
            </Button>
            <Button
              variant="outline"
              className="h-10 justify-start"
              disabled={
                !activeRow?.seedCounts?.Microsoft365 ||
                !!(activeRow && downloadingIds.has(`${activeRow.id}-Microsoft-seeds`))
              }
              onClick={() => {
                if (!activeRow) return;
                handleDownload(
                  activeRow.id,
                  activeRow.email,
                  "Microsoft365",
                  activeRow.replacementAvailability?.Microsoft365 || false,
                  false
                );
              }}
            >
              {activeRow && downloadingIds.has(`${activeRow.id}-Microsoft-seeds`) ? (
                <span className="h-4 w-4 mr-2 inline-block animate-spin rounded-full border-2 border-current border-r-transparent" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {activeRow && downloadingIds.has(`${activeRow.id}-Microsoft-seeds`) ? "Downloading..." : "Microsoft365 Seeds"}
            </Button>
          </div>


          <DialogFooter className="mt-4 w-full flex justify-between sm:justify-between items-center">
             {/* Download all seeds */}
          <Button
            disabled={
              !(
                (activeRow?.seedCounts?.gmail || 0) +
                (activeRow?.seedCounts?.outlook || 0) +
                (activeRow?.seedCounts?.yahoo || 0) +
                (activeRow?.seedCounts?.["Google-Workspace"] || 0) +
                (activeRow?.seedCounts?.Microsoft365 || 0)
              ) || !!(activeRow && downloadingAll.has(`${activeRow.id}-all-seeds`))
            }
            onClick={() => {
              if (!activeRow) return;
              handleDownloadAllSeeds(activeRow.id, activeRow.email);
            }}
          >
            {activeRow && downloadingAll.has(`${activeRow.id}-all-seeds`) ? (
              <span className="h-4 w-4 mr-2 inline-block animate-spin rounded-full border-2 border-current border-r-transparent" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            {activeRow && downloadingAll.has(`${activeRow.id}-all-seeds`) ? "Preparing..." : "Download All Seeds"}
          </Button>
            <Button
              variant="secondary"
              onClick={() => setIsDownloadDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replacements dialog */}
      <Dialog open={isReplacementDialogOpen} onOpenChange={setIsReplacementDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Download Replacements</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              variant="outline"
              className="h-10 justify-start"
              disabled={
                !activeRow?.replacementAvailability?.gmail ||
                !!(activeRow && downloadingIds.has(`${activeRow.id}-Gmail-replacement`))
              }
              onClick={() => {
                if (!activeRow) return;
                handleDownload(activeRow.id, activeRow.email, "Gmail", true, true);
              }}
            >
              {activeRow && downloadingIds.has(`${activeRow.id}-Gmail-replacement`) ? (
                <span className="h-4 w-4 mr-2 inline-block animate-spin rounded-full border-2 border-current border-r-transparent" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {activeRow && downloadingIds.has(`${activeRow.id}-Gmail-replacement`) ? "Downloading..." : "Gmail Replacements"}
            </Button>
            <Button
              variant="outline"
              className="h-10 justify-start"
              disabled={
                !activeRow?.replacementAvailability?.outlook ||
                !!(activeRow && downloadingIds.has(`${activeRow.id}-Outlook-replacement`))
              }
              onClick={() => {
                if (!activeRow) return;
                handleDownload(activeRow.id, activeRow.email, "Outlook", true, true);
              }}
            >
              {activeRow && downloadingIds.has(`${activeRow.id}-Outlook-replacement`) ? (
                <span className="h-4 w-4 mr-2 inline-block animate-spin rounded-full border-2 border-current border-r-transparent" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {activeRow && downloadingIds.has(`${activeRow.id}-Outlook-replacement`) ? "Downloading..." : "Outlook Replacements"}
            </Button>
            <Button
              variant="outline"
              className="h-10 justify-start"
              disabled={
                !activeRow?.replacementAvailability?.yahoo ||
                !!(activeRow && downloadingIds.has(`${activeRow.id}-Yahoo-replacement`))
              }
              onClick={() => {
                if (!activeRow) return;
                handleDownload(activeRow.id, activeRow.email, "Yahoo", true, true);
              }}
            >
              {activeRow && downloadingIds.has(`${activeRow.id}-Yahoo-replacement`) ? (
                <span className="h-4 w-4 mr-2 inline-block animate-spin rounded-full border-2 border-current border-r-transparent" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {activeRow && downloadingIds.has(`${activeRow.id}-Yahoo-replacement`) ? "Downloading..." : "Yahoo Replacements"}
            </Button>
            <Button
              variant="outline"
              className="h-10 justify-start overflow-hidden"
              disabled={
                !activeRow?.replacementAvailability?.["Google-Workspace"] ||
                !!(activeRow && downloadingIds.has(`${activeRow.id}-Google-Workspace-replacement`))
              }
              onClick={() => {
                if (!activeRow) return;
                handleDownload(activeRow.id, activeRow.email, "Google-Workspace", true, true);
              }}
            >
              {activeRow && downloadingIds.has(`${activeRow.id}-Google-Workspace-replacement`) ? (
                <span className="h-4 w-4 mr-2 inline-block animate-spin rounded-full border-2 border-current border-r-transparent" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {activeRow && downloadingIds.has(`${activeRow.id}-Google-Workspace-replacement`) ? "Downloading..." : "Google Workspace Replacements"}
            </Button>
            <Button
              variant="outline"
              className="h-10 justify-start"
              disabled={
                !activeRow?.replacementAvailability?.Microsoft365 ||
                !!(activeRow && downloadingIds.has(`${activeRow.id}-Microsoft365-replacement`))
              }
              onClick={() => {
                if (!activeRow) return;
                handleDownload(activeRow.id, activeRow.email, "Microsoft365", true, true);
              }}
            >
              {activeRow && downloadingIds.has(`${activeRow.id}-Microsoft365-replacement`) ? (
                <span className="h-4 w-4 mr-2 inline-block animate-spin rounded-full border-2 border-current border-r-transparent" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {activeRow && downloadingIds.has(`${activeRow.id}-Microsoft365-replacement`) ? "Downloading..." : "Microsoft365 Replacements"}
            </Button>
          </div>

          <DialogFooter className="mt-4 w-full flex justify-between sm:justify-between items-center">

            {/* Download all replacements */}
          <Button
            disabled={
              !(
                activeRow?.replacementAvailability?.gmail ||
                activeRow?.replacementAvailability?.outlook ||
                activeRow?.replacementAvailability?.yahoo ||
                activeRow?.replacementAvailability?.["Google-Workspace"] ||
                activeRow?.replacementAvailability?.Microsoft365
              ) || !!(activeRow && downloadingAll.has(`${activeRow.id}-all-replacements`))
            }
            onClick={() => {
              if (!activeRow) return;
              handleDownloadAllReplacements(activeRow.id, activeRow.email);
            }}
          >
            {activeRow && downloadingAll.has(`${activeRow.id}-all-replacements`) ? (
              <span className="h-4 w-4 mr-2 inline-block animate-spin rounded-full border-2 border-current border-r-transparent" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            {activeRow && downloadingAll.has(`${activeRow.id}-all-replacements`) ? "Preparing..." : "Download All Replacements Seeds"}
          </Button>
            <Button
              variant="secondary"
              onClick={() => setIsReplacementDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
